<template>
  <div class="family-auth family-page w-full bg-gradient-to-b from-[#F0F7FC] via-white to-slate-50">
    <header
      class="family-page-header px-4 py-3 flex items-center"
      :style="{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top))' }"
    >
      <button
        type="button"
        class="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center text-slate-500 shadow-sm"
        aria-label="返回"
        @click="$router.push('/family/login')"
      >
        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </header>

    <main class="family-page-body px-5 pb-8">
      <div class="flex items-center gap-3 mb-8">
        <img :src="brandLogo" alt="安心陪护" class="family-brand-logo" />
        <div>
          <h1 class="text-xl font-bold text-slate-900">安心陪护</h1>
          <p class="text-xs text-slate-400 mt-0.5">家属端 · 注册</p>
        </div>
      </div>

      <div class="bg-white rounded-2xl p-5 shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100/60">
        <h2 class="text-lg font-bold text-slate-800 mb-1">创建账号</h2>
        <p class="text-sm text-slate-400 mb-4">注册后即可提交陪护需求、跟踪订单</p>

        <div class="flex items-start gap-2 rounded-xl bg-blue-50 border border-blue-100 px-3.5 py-3 mb-5">
          <svg class="flex-shrink-0 w-4 h-4 mt-0.5 text-family-blue" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p class="text-xs text-slate-600 leading-relaxed">
            短信验证码暂未开通，请直接填写固定验证码
            <span class="text-family-blue font-bold text-sm">{{ fixedSmsCode }}</span>
            完成注册（点击「获取验证码」也会提示相同验证码）
          </p>
        </div>

        <form @submit.prevent="handleRegister">
          <label class="block mb-4">
            <span class="text-sm text-slate-600">手机号 <span class="text-red-500">*</span></span>
            <input
              v-model.trim="form.mobile"
              type="tel"
              maxlength="11"
              required
              placeholder="请输入手机号"
              class="auth-field"
            />
          </label>

          <label class="block mb-4">
            <span class="text-sm text-slate-600">验证码 <span class="text-red-500">*</span></span>
            <div class="flex gap-2 mt-1.5">
              <input
                v-model.trim="form.smsCode"
                type="text"
                maxlength="6"
                required
                placeholder="6 位验证码"
                class="auth-field flex-1 min-w-0"
              />
              <button
                type="button"
                class="flex-shrink-0 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors min-h-[46px]"
                :class="smsCooldown > 0 ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-family-blue'"
                :disabled="smsCooldown > 0 || smsSending"
                @click="sendSms"
              >
                {{ smsCooldown > 0 ? `${smsCooldown}s` : smsSending ? "发送中" : "获取验证码" }}
              </button>
            </div>
            <button
              type="button"
              class="mt-2 text-xs text-family-blue font-medium"
              @click="fillFixedCode"
            >
              一键填入固定验证码 {{ fixedSmsCode }}
            </button>
          </label>

          <label class="block mb-4">
            <span class="text-sm text-slate-600">昵称</span>
            <input
              v-model.trim="form.nickname"
              type="text"
              maxlength="20"
              placeholder="如：李女士（选填）"
              class="auth-field"
            />
          </label>

          <label class="block mb-4">
            <span class="text-sm text-slate-600">设置密码 <span class="text-red-500">*</span></span>
            <input
              v-model="form.password"
              type="password"
              required
              placeholder="6-20 位密码"
              class="auth-field"
            />
          </label>

          <label class="block mb-5">
            <span class="text-sm text-slate-600">确认密码 <span class="text-red-500">*</span></span>
            <input
              v-model="form.confirmPassword"
              type="password"
              required
              placeholder="再次输入密码"
              class="auth-field"
            />
          </label>

          <label class="flex items-start gap-2 mb-6 cursor-pointer">
            <input
              v-model="agreed"
              type="checkbox"
              class="mt-0.5 rounded border-slate-300 text-family-blue focus:ring-family-blue"
            />
            <span class="text-xs text-slate-500 leading-relaxed">
              我已阅读并同意
              <span class="text-family-blue">《用户服务协议》</span>
              和
              <span class="text-family-blue">《隐私政策》</span>
            </span>
          </label>

          <button
            type="submit"
            class="w-full py-3.5 rounded-xl text-white text-sm font-semibold bg-family-blue shadow-[0_6px_20px_rgb(0,130,212,0.35)] transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
            :disabled="loading"
          >
            <svg v-if="loading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ loading ? "注册中..." : "注册" }}
          </button>
        </form>

        <p class="text-center text-sm text-slate-500 mt-6">
          已有账号？
          <button type="button" class="text-family-blue font-medium" @click="$router.push('/family/login')">
            去登录
          </button>
        </p>
      </div>
    </main>
  </div>
</template>

<script>
import { familyAuth, FAMILY_FIXED_SMS_CODE } from "./familyAuth";
import brandLogo from "../../common/brandLogo";

export default {
  name: "FamilyRegister",
  data() {
    return {
      brandLogo,
      fixedSmsCode: FAMILY_FIXED_SMS_CODE,
      form: {
        mobile: "",
        smsCode: "",
        nickname: "",
        password: "",
        confirmPassword: "",
      },
      agreed: false,
      loading: false,
      smsSending: false,
      smsCooldown: 0,
      smsTimer: null,
    };
  },
  mounted() {
    document.title = "注册 - 安心陪护";
    document.documentElement.classList.add("family-route");
    document.body.classList.add("family-route");
    if (familyAuth.isLoggedIn()) {
      this.$router.replace("/family");
    }
  },
  beforeDestroy() {
    document.documentElement.classList.remove("family-route");
    document.body.classList.remove("family-route");
    if (this.smsTimer) clearInterval(this.smsTimer);
  },
  methods: {
    fillFixedCode() {
      this.form.smsCode = this.fixedSmsCode;
      this.$message.success(`已填入固定验证码 ${this.fixedSmsCode}`);
    },
    async sendSms() {
      if (!/^1\d{10}$/.test(this.form.mobile)) {
        this.$message.warning("请输入正确的手机号");
        return;
      }
      if (this.smsCooldown > 0 || this.smsSending) return;
      this.smsSending = true;
      try {
        await familyAuth.sendSmsCode(this.form.mobile);
        this.form.smsCode = this.fixedSmsCode;
        this.smsCooldown = 60;
        this.smsTimer = setInterval(() => {
          this.smsCooldown -= 1;
          if (this.smsCooldown <= 0) {
            clearInterval(this.smsTimer);
            this.smsTimer = null;
          }
        }, 1000);
        this.$message.success(`演示验证码：${this.fixedSmsCode}（已自动填入）`);
      } catch (e) {
        this.$message.error((e && e.message) || "验证码发送失败");
      } finally {
        this.smsSending = false;
      }
    },
    async handleRegister() {
      if (this.loading) return;
      if (!/^1\d{10}$/.test(this.form.mobile)) {
        this.$message.warning("请输入正确的手机号");
        return;
      }
      if (!this.form.smsCode) {
        this.$message.warning("请输入验证码");
        return;
      }
      if (this.form.password.length < 6 || this.form.password.length > 20) {
        this.$message.warning("密码长度为 6-20 位");
        return;
      }
      if (this.form.password !== this.form.confirmPassword) {
        this.$message.warning("两次输入的密码不一致");
        return;
      }
      if (!this.agreed) {
        this.$message.warning("请先阅读并同意服务协议与隐私政策");
        return;
      }
      this.loading = true;
      try {
        const result = await familyAuth.register({
          mobile: this.form.mobile,
          smsCode: this.form.smsCode,
          nickname: this.form.nickname,
          password: this.form.password,
        });
        if (!result.ok) {
          this.$message.error(result.message);
          return;
        }
        this.$message.success("注册成功，已自动登录");
        this.$router.replace("/family");
      } catch (e) {
        this.$message.error((e && e.message) || "注册失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.family-auth {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}
.auth-field {
  margin-top: 0.375rem;
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background-color: #f1f5f9;
  font-size: 0.875rem;
  color: #1e293b;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.auth-field::placeholder {
  color: #94a3b8;
}
.auth-field:focus {
  outline: none;
  border-color: #0082d4;
  box-shadow: 0 0 0 4px rgba(0, 130, 212, 0.1);
  background-color: #fff;
}
</style>
